import React, { useState } from "react";
import { SegmentedControl, styles as ui } from "stories/Admin";
import {
  ItemForm,
  KidsDua,
  LEARN_LABELS,
  phraseMeaning,
  previewCard,
  PreviewLang,
} from "./kidsTypes";
import styles from "./KidsPage.module.scss";

interface Props {
  form: ItemForm;
  dua: KidsDua | null;
  levelColor?: string | null;
  defaultAdhanAudioUrl?: string | null;
}

const LANGS: Array<{ value: PreviewLang; label: string }> = [
  { value: "nb", label: "Norsk" },
  { value: "en", label: "English" },
  { value: "ur", label: "اردو" },
  { value: "ar", label: "عربي" },
];

/** «Hør»-knappen på lærekortet: spiller av og stopper lyden. */
const HearButton: React.FC<{
  url: string | null;
  label?: string;
  small?: boolean;
}> = ({ url, label = "Hør", small }) => {
  const [audio] = useState(() =>
    typeof Audio !== "undefined" ? new Audio() : null,
  );
  const [playing, setPlaying] = useState(false);
  if (!url || !audio) return null;
  const toggle = () => {
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    if (audio.src !== url) audio.src = url;
    audio.currentTime = 0;
    audio.onended = () => setPlaying(false);
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false));
  };
  return (
    <button
      type="button"
      className={small ? styles.hearSmall : styles.hear}
      onClick={toggle}
      aria-pressed={playing}
    >
      <span aria-hidden="true">{playing ? "⏸" : "🔊"}</span>{" "}
      {playing ? "Stopp" : label}
    </button>
  );
};

/** Forhåndsvisning som ligner lærekortet i appen (stor arabisk, transliterasjon, tekst og «Hør»). */
const KidsPreview: React.FC<Props> = ({
  form,
  dua,
  levelColor,
  defaultAdhanAudioUrl,
}) => {
  const [lang, setLang] = useState<PreviewLang>("nb");
  const card = previewCard(form, dua, lang, defaultAdhanAudioUrl);
  const rtl = lang === "ur" || lang === "ar";

  return (
    <div className={styles.previewWrap}>
      <SegmentedControl
        label="Språk i forhåndsvisningen"
        options={LANGS}
        value={lang}
        onChange={setLang}
      />
      <div
        className={`${styles.phone} ${styles[`tone_${levelColor || "mint"}`] ?? ""}`}
      >
        <div className={styles.cardTop} dir={rtl ? "rtl" : "ltr"}>
          <span className={styles.cardEmoji} aria-hidden="true">
            {form.emoji || "⭐"}
          </span>
          <h3 className={styles.cardTitle}>
            {card.title || "Tittel for barn"}
          </h3>
          <span
            className={styles.cardStars}
            aria-label={`${form.stars} stjerner`}
          >
            {"★".repeat(Math.max(0, Math.min(form.stars, 10)))}
          </span>
        </div>

        {form.type === "DUA" && (
          <div className={styles.cardBody}>
            {card.arabic ? (
              <p
                className={`${ui.arabic} ${styles.cardArabic}`}
                lang="ar"
                dir="rtl"
              >
                {card.arabic}
              </p>
            ) : (
              <p className={styles.muted}>Velg en dua for å se teksten.</p>
            )}
            {card.transliteration && (
              <p className={styles.cardTranslit}>{card.transliteration}</p>
            )}
            {card.meaning && (
              <p className={styles.cardMeaning} dir={rtl ? "rtl" : "ltr"}>
                {card.meaning}
              </p>
            )}
          </div>
        )}

        {form.type === "ADHAN" && (
          <ol className={styles.cardPhrases}>
            {form.phrases.map((p, i) => (
              <li
                key={i}
                className={p.fajrOnly ? styles.cardPhraseFajr : undefined}
              >
                <div className={styles.cardPhraseHead}>
                  <span className={styles.cardRepeat}>×{p.repeat}</span>
                  {p.fajrOnly && (
                    <span className={styles.cardFajr}>Bare til Fajr</span>
                  )}
                  <HearButton url={p.audioUrl || null} small />
                </div>
                <p
                  className={`${ui.arabic} ${styles.cardPhraseArabic}`}
                  lang="ar"
                  dir="rtl"
                >
                  {p.arabic}
                </p>
                {p.transliteration && (
                  <p className={styles.cardTranslit}>{p.transliteration}</p>
                )}
                <p className={styles.cardMeaning} dir={rtl ? "rtl" : "ltr"}>
                  {phraseMeaning(p, lang)}
                </p>
              </li>
            ))}
          </ol>
        )}

        {form.type === "SURAH" && (
          <p className={styles.cardInfo}>
            📖 Sura {form.surahNumber || "?"}: ayahene og lyden hentes fra
            Koranen i appen.
          </p>
        )}
        {form.type === "LEARN" && (
          <p className={styles.cardInfo}>
            🧎 Åpner «{LEARN_LABELS[form.learnKey] ?? "Lær å be"}» i appen.
          </p>
        )}

        {card.text && (
          <p className={styles.cardText} dir={rtl ? "rtl" : "ltr"}>
            {card.text}
          </p>
        )}

        <div className={styles.cardActions}>
          <HearButton
            url={card.audioUrl}
            label={form.type === "ADHAN" ? "Hør hele adhan" : "Hør"}
          />
          <span className={styles.cardDone}>Jeg kan den!</span>
        </div>
        <p className={styles.cardSource}>
          Lyd: {card.audioSource}
          {card.reference && ` · ${card.reference}`}
        </p>
      </div>
    </div>
  );
};

export default KidsPreview;
